import { useTranslation } from 'react-i18next';
import { formatDate, timeAgo } from '../../utils/dateUtils';
import TablePagination from './TablePagination';

function initials(username) {
  return username ? username.slice(0, 2).toUpperCase() : '??';
}

function AccountsTable({
  title,
  tint = 'lavender',
  accounts,
  currentUsername = null,
  showActions = false,
  showSubscription = false,
  showLastLogin = false,
  showOpenReports = false,
  openReportUserIds,
  onEdit,
  onDelete,
  onRowClick,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  emptyMessage,
  pagination,
  fullWidth = false,
}) {
  const { t } = useTranslation('admin');
  const columnCount = 3 + (showSubscription ? 1 : 0) + (showOpenReports ? 1 : 0) + (showLastLogin ? 1 : 0) + (showActions ? 1 : 0);
  const searchable = onSearchChange !== undefined;
  const resolvedSearchPlaceholder = searchPlaceholder ?? t('accountsTable.searchByName');
  const resolvedEmptyMessage = emptyMessage ?? t('accountsTable.noAccountsYet');

  return (
    <div className={`sheet-table-card${fullWidth ? ' sheet-table-card--full' : ''}`}>
      <div className={`sheet-panel-header tint-${tint}`}>
        <span>{title}</span>
        {searchable && (
          <input
            type="search"
            className="table-search-input"
            placeholder={resolvedSearchPlaceholder}
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            aria-label={`${title} search`}
          />
        )}
      </div>
      <div className="table-scroll-x">
      <table className="sheet-table account-table" style={{ tableLayout: 'fixed', minWidth: '560px' }}>
        <colgroup>
          <col />
          <col style={{ width: '90px' }} />
          <col style={{ width: '100px' }} />
          {showSubscription && <col style={{ width: '130px' }} />}
          {showOpenReports && <col style={{ width: '130px' }} />}
          {showLastLogin && <col style={{ width: '110px' }} />}
          {showActions && <col style={{ width: '90px' }} />}
        </colgroup>
        <thead>
          <tr>
            <th>{t('accountsTable.account')}</th>
            <th>{t('accountsTable.role')}</th>
            <th>{t('accountsTable.joined')}</th>
            {showSubscription && <th>{t('accountsTable.subscription')}</th>}
            {showOpenReports && <th>{t('accountsTable.reports')}</th>}
            {showLastLogin && <th>{t('accountsTable.lastLogin')}</th>}
            {showActions && <th aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {accounts.length === 0 && (
            <tr className="sheet-empty-row"><td colSpan={columnCount}>{resolvedEmptyMessage}</td></tr>
          )}
          {accounts.map((account) => (
            <tr
              key={account.id || account.username}
              className={onRowClick ? 'account-row-clickable' : undefined}
              onClick={onRowClick ? () => onRowClick(account) : undefined}
            >
              <td>
                <div className="account-cell">
                  {account.avatarUrl ? (
                    <img src={account.avatarUrl} alt="" className="profile-avatar profile-avatar-img profile-avatar-small" />
                  ) : (
                    <span className="profile-avatar profile-avatar-small">{initials(account.username)}</span>
                  )}
                  <span className="account-username">{account.username}</span>
                  {currentUsername && account.username === currentUsername && (
                    <span className="account-you-badge">{t('you')}</span>
                  )}
                </div>
              </td>
              <td><span className={`role-pill ${account.role}`}>{t(`roleLabels.${account.role}`)}</span></td>
              <td className="account-joined">{account.createdAt ? formatDate(account.createdAt) : t('dash')}</td>
              {showSubscription && (
                <td><span className={`package-pill ${account.subscriptionPackage || 'free'}`}>{t(`packageLabels.${account.subscriptionPackage || 'free'}`)}</span></td>
              )}
              {showOpenReports && (
                <td>
                  {openReportUserIds?.has(account.id) ? (
                    <span className="report-flag">{t('accountsTable.unresolved')}</span>
                  ) : (
                    <span className="report-flag-clear">{t('dash')}</span>
                  )}
                </td>
              )}
              {showLastLogin && (
                <td className="account-joined">{account.lastLoginAt ? timeAgo(account.lastLoginAt) : t('never')}</td>
              )}
              {showActions && (
                <td>
                  <div className="goal-actions">
                    <button onClick={(event) => { event.stopPropagation(); onEdit(account); }} className="text-btn icon-btn" aria-label="Edit">✎</button>
                    <button onClick={(event) => { event.stopPropagation(); onDelete(account); }} className="text-btn danger icon-btn" aria-label="Delete">✕</button>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {pagination && <TablePagination page={pagination.page} totalPages={pagination.totalPages} onChange={pagination.onChange} />}
    </div>
  );
}

export default AccountsTable;
